import { useCallback, useEffect, useState } from 'react';

/**
 * Tracks whether an Unsplash photo is actually ON SCREEN right now.
 *
 * The obvious version of this — "does any activity carry a `pool` field?" —
 * credits the wrong thing. The API ships every rung of the ladder together, and
 * the client picks one: an item with `{user_photo_url, pool}` displays the
 * user's own photograph, and an item with `{place_id, pool}` displays Google's
 * when the venue rung resolves. Reading the payload, a plan whose only pictures
 * are the user's own still says "Photos by Unsplash" — attributing someone's
 * personal photo to a stock library.
 *
 * The rung that WINS is only known inside the hook that resolves it, and it can
 * change after mount (a venue photo that 404s demotes to the pool). So each row
 * reports the tier it settled on and this counts them.
 *
 * `report` is stable and returns the previous set unchanged when nothing moved,
 * so a row re-reporting the same tier cannot drive a render loop.
 */
export function useUnsplashCredit() {
  const [credited, setCredited] = useState<ReadonlySet<number>>(() => new Set());

  const report = useCallback((itemId: number, tier: string | undefined) => {
    setCredited((prev) => {
      const isPool = tier === 'pool';
      if (isPool === prev.has(itemId)) return prev;
      const next = new Set(prev);
      if (isPool) next.add(itemId);
      else next.delete(itemId);
      return next;
    });
  }, []);

  return { anyPoolPhotoShown: credited.size > 0, report };
}

/**
 * Reports the rung one row is showing, and WITHDRAWS it when the row goes away.
 *
 * The withdrawal is the half that is easy to miss, and it fails the same way
 * reading the payload did: collapsing a day, deleting an activity, or replacing
 * a thumbnail with a local upload unmounts the row while its id stays credited,
 * so the footer keeps crediting Unsplash for a photograph no longer on screen.
 *
 * A hook rather than two effects written out at the call site, so the pair
 * cannot be half-copied to the next surface that needs it — and so the unmount
 * behaviour is testable without mounting a whole itinerary page.
 */
export function useReportedPhotoTier(
  itemId: number,
  tier: string | undefined,
  report?: (itemId: number, tier: string | undefined) => void,
): void {
  useEffect(() => {
    report?.(itemId, tier);
  }, [report, itemId, tier]);

  useEffect(() => () => report?.(itemId, undefined), [report, itemId]);
}

export default useUnsplashCredit;
